import axios from "axios";
import { toast } from "sonner";

const baseURL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({ baseURL });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("ps_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// URLs that mutate credit / usage state — every successful response triggers
// a global "scrapecore:usage_changed" event that all dashboards subscribe to.
const USAGE_MUTATING = [
  "/scrape",
  "/scrape/batch",
  "/scrape/screenshot",
  "/scrape/extract",
  "/scrape/async",
  "/scrape/markdown",
  "/scrape/twitter",
  "/billing/upgrade",
  "/billing/auto-renew",
  "/billing/cancel",
  "/billing/razorpay/verify",
  "/billing/razorpay/subscription/verify",
];

// Suppress 401 toast for these endpoints (they handle auth themselves)
const SILENT_401 = ["/auth/login", "/auth/register", "/auth/2fa/verify", "/auth/me"];

api.interceptors.response.use(
  (r) => {
    try {
      const url = r?.config?.url || "";
      if (USAGE_MUTATING.some((p) => url.startsWith(p) || url === p)) {
        window.dispatchEvent(new CustomEvent("scrapecore:usage_changed", { detail: { url, data: r.data } }));
      }
    } catch (_) {}
    return r;
  },
  (err) => {
    const url = err?.config?.url || "";
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail || "";

    if (status === 401 && !SILENT_401.some(p => url.includes(p))) {
      const isRevoked = typeof detail === "string" && detail.toLowerCase().includes("revoked");
      if (isRevoked) {
        localStorage.removeItem("ps_token");
        toast.error("Your session has been revoked. Please sign in again.", {
          id: "session-revoked",
          duration: 5000,
          action: { label: "Sign In", onClick: () => { window.location.href = "/login"; } },
        });
        window.dispatchEvent(new CustomEvent("scrapecore:session_revoked"));
      }
    }

    // Account suspension — refresh user context so the banner appears immediately
    const detailObj = typeof detail === "object" ? detail : {};
    if (status === 403 && (detailObj.error === "account_suspended" || (typeof detail === "string" && detail.toLowerCase().includes("suspended")))) {
      toast.error(detailObj.suspension_reason
        ? `Account suspended: ${detailObj.suspension_reason}`
        : "Your account is suspended. Contact support.", { id: "account-suspended", duration: 6000 });
      // Dispatch usage-changed so AuthContext re-fetches /auth/me and shows banner
      window.dispatchEvent(new CustomEvent("scrapecore:usage_changed", { detail: { url, data: {} } }));
    }

    // Maintenance mode detection
    if (status === 503 && err?.response?.data?.maintenance_mode) {
      window.dispatchEvent(new CustomEvent("scrapecore:maintenance"));
    }
    return Promise.reject(err);
  }
);

/** Subscribe to usage-changed events. Returns an unsubscribe function. */
export function onUsageChanged(handler) {
  const fn = (e) => handler(e.detail);
  window.addEventListener("scrapecore:usage_changed", fn);
  return () => window.removeEventListener("scrapecore:usage_changed", fn);
}

export function formatErr(e) {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Request failed";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  // Structured error object (e.g. rate limit detail with .message field)
  if (typeof d === "object" && d !== null) {
    if (d.message) return d.message;
    return JSON.stringify(d);
  }
  return String(d);
}
